// Read-only diagnostics viewer for the two OZ data collections that feed the
// engine but have no edit UI: historicalEarnings (12-month history driving
// TYPE_40/averages) and absoftMonthlyTargets (AB Soft IA43 targets the shadow
// compares against). Purely diagnostic — helps explain shadow diffs. No edits.

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Segmented,
  Table,
  Input,
  Select,
  Space,
  Tag,
  Alert,
  Empty,
} from 'antd';

import {
  getHistoricalEarnings,
  getAbsoftTargets,
} from '@/services/payrollEngine.service';

const empName = (e) =>
  e && (e.firstName || e.lastName)
    ? `${e.lastName || ''} ${e.firstName || ''}`.trim()
    : '—';

const empJmbg = (e) => e?.generalData?.personalNumber || e?.jmbg || '';

const num = (v) =>
  v == null ? '—' : Number(v).toLocaleString('sr-RS', { maximumFractionDigits: 2 });

// Sum a component field; used as a fallback when the roll-up total field is
// missing on the stored doc (recent months seeded with components but no total).
const sumComp = (components, field) =>
  (components || []).reduce((acc, c) => acc + (Number(c[field]) || 0), 0);

// Prefer the stored roll-up total; derive from components when it is absent.
const totalOr = (record, totalField, compField) =>
  record[totalField] != null ? record[totalField] : sumComp(record.components, compField);

function HistoricalEarningsView({ companyId }) {
  const { t } = useTranslation('forms');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [yearFilter, setYearFilter] = useState(null);
  const [monthFilter, setMonthFilter] = useState(null);

  const reload = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getHistoricalEarnings(companyId);
      setItems(res?.items || []);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const years = useMemo(
    () => [...new Set(items.map((i) => i.year))].sort((a, b) => b - a),
    [items]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((i) => {
      if (yearFilter && i.year !== yearFilter) return false;
      if (monthFilter && i.month !== monthFilter) return false;
      if (q) {
        const name = empName(i.employee).toLowerCase();
        const jmbg = empJmbg(i.employee);
        if (!name.includes(q) && !jmbg.includes(q)) return false;
      }
      return true;
    });
  }, [items, search, yearFilter, monthFilter]);

  const columns = [
    { title: t('ozColEmployee'), key: 'emp', render: (_, r) => empName(r.employee) },
    { title: t('ozColJmbg'), key: 'jmbg', width: 130, render: (_, r) => empJmbg(r.employee) || '—' },
    { title: t('ozColYear'), dataIndex: 'year', width: 70 },
    { title: t('ozColMonth'), dataIndex: 'month', width: 60 },
    { title: t('ozColContractGross'), dataIndex: 'contractGross', align: 'right', render: num },
    { title: t('ozColSumGross'), key: 'tg', align: 'right', render: (_, r) => num(totalOr(r, 'totalGross', 'amount')) },
    { title: t('ozColSumNet'), key: 'tn', align: 'right', render: (_, r) => num(totalOr(r, 'totalNet', 'net')) },
    { title: t('ozColSumHours'), key: 'th', align: 'right', render: (_, r) => num(totalOr(r, 'totalHours', 'hours')) },
    {
      title: t('ozColSource'),
      dataIndex: 'source',
      width: 150,
      render: (v) => {
        const color =
          v === 'absoft_extract'
            ? 'blue'
            : v === 'hlb_payroll_run'
              ? 'green'
              : 'orange';
        return v ? <Tag color={color}>{v}</Tag> : '—';
      },
    },
  ];

  const expanded = (record) => {
    const comps = record.components || [];
    if (!comps.length)
      return <span className="text-xs text-gray-400">{t('ozDiagNoComponents')}</span>;
    return (
      <Table
        size="small"
        rowKey={(r, idx) => `${r.earningCode}-${idx}`}
        pagination={false}
        dataSource={comps}
        columns={[
          { title: t('ozColCode'), dataIndex: 'earningCode', width: 80 },
          { title: t('ozColHours'), dataIndex: 'hours', align: 'right', render: num },
          { title: t('ozColGross'), dataIndex: 'amount', align: 'right', render: num },
          { title: t('ozColNet'), dataIndex: 'net', align: 'right', render: num },
          { title: t('ozColGrossMinB'), dataIndex: 'grossWithMinB', align: 'right', render: num },
          { title: t('ozColNetMinB'), dataIndex: 'netWithMinB', align: 'right', render: num },
        ]}
      />
    );
  };

  return (
    <div>
      {error && <Alert type="error" showIcon message={error} className="mb-2" />}
      <Space className="mb-2" wrap>
        <Input.Search
          allowClear
          placeholder={t('ozDiagSearchNameJmbg')}
          style={{ width: 240 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select
          allowClear
          placeholder={t('ozDiagYear')}
          style={{ width: 110 }}
          value={yearFilter}
          onChange={setYearFilter}
          options={years.map((y) => ({ label: y, value: y }))}
        />
        <Select
          allowClear
          placeholder={t('ozDiagMonth')}
          style={{ width: 100 }}
          value={monthFilter}
          onChange={setMonthFilter}
          options={Array.from({ length: 12 }, (_, i) => ({ label: i + 1, value: i + 1 }))}
        />
        <span className="text-xs text-gray-400">{t('ozDiagRecords', { count: filtered.length })}</span>
      </Space>
      <Table
        size="small"
        rowKey="_id"
        loading={loading}
        dataSource={filtered}
        columns={columns}
        expandable={{ expandedRowRender: expanded }}
        pagination={{ pageSize: 50, showSizeChanger: true }}
        locale={{ emptyText: <Empty description={t('ozDiagEmptyHistory')} /> }}
      />
    </div>
  );
}

function AbsoftTargetsView({ companyId }) {
  const { t } = useTranslation('forms');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [godMesFilter, setGodMesFilter] = useState(null);

  const reload = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getAbsoftTargets(companyId);
      setItems(res?.items || []);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const godMesOptions = useMemo(
    () => [...new Set(items.map((i) => i.godMes))].sort().reverse(),
    [items]
  );

  const sumField = (target, f) =>
    (target?.targets || []).reduce((acc, x) => acc + (Number(x[f]) || 0), 0);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((i) => {
      if (godMesFilter && i.godMes !== godMesFilter) return false;
      if (q) {
        const name = empName(i.employee).toLowerCase();
        const matic = i.matic || '';
        if (!name.includes(q) && !matic.includes(q)) return false;
      }
      return true;
    });
  }, [items, search, godMesFilter]);

  const columns = [
    { title: t('ozColEmployee'), key: 'emp', render: (_, r) => empName(r.employee) },
    { title: t('ozColMatic'), dataIndex: 'matic', width: 130 },
    { title: t('ozColGodMes'), dataIndex: 'godMes', width: 90 },
    { title: t('ozColVrsta'), dataIndex: 'vrsta', width: 70 },
    { title: t('ozColIsplata'), dataIndex: 'isplata', width: 80 },
    { title: t('ozColSumGross'), key: 'br', align: 'right', render: (_, r) => num(sumField(r, 'bruto')) },
    { title: t('ozColSumNet'), key: 'net', align: 'right', render: (_, r) => num(sumField(r, 'neto')) },
    { title: t('ozColSumHours'), key: 'sati', align: 'right', render: (_, r) => num(sumField(r, 'sati')) },
  ];

  const expanded = (record) => (
    <Table
      size="small"
      rowKey={(r, idx) => `${r.dopGrupa}-${r.porez}-${idx}`}
      pagination={false}
      dataSource={record.targets || []}
      columns={[
        { title: t('ozColDopGrupa'), dataIndex: 'dopGrupa', width: 90 },
        { title: t('ozColPorez'), dataIndex: 'porez', width: 80 },
        { title: t('ozColGross'), dataIndex: 'bruto', align: 'right', render: num },
        { title: t('ozColNet'), dataIndex: 'neto', align: 'right', render: num },
        { title: t('ozColIznMinB'), dataIndex: 'iznMinB', align: 'right', render: num },
        { title: t('ozColKorekOsnov'), dataIndex: 'korekOsnov', align: 'right', render: num },
        { title: t('ozColHours'), dataIndex: 'sati', align: 'right', render: num },
      ]}
    />
  );

  return (
    <div>
      {error && <Alert type="error" showIcon message={error} className="mb-2" />}
      <Space className="mb-2" wrap>
        <Input.Search
          allowClear
          placeholder={t('ozDiagSearchNameMatic')}
          style={{ width: 240 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select
          allowClear
          placeholder={t('ozColGodMes')}
          style={{ width: 120 }}
          value={godMesFilter}
          onChange={setGodMesFilter}
          options={godMesOptions.map((g) => ({ label: g, value: g }))}
        />
        <span className="text-xs text-gray-400">{t('ozDiagRecords', { count: filtered.length })}</span>
      </Space>
      <Table
        size="small"
        rowKey="_id"
        loading={loading}
        dataSource={filtered}
        columns={columns}
        expandable={{ expandedRowRender: expanded }}
        pagination={{ pageSize: 50, showSizeChanger: true }}
        locale={{ emptyText: <Empty description={t('ozDiagEmptyTargets')} /> }}
      />
    </div>
  );
}

export default function OzDiagnostics({ companyId }) {
  const { t } = useTranslation('forms');
  const [mode, setMode] = useState('history');

  if (!companyId) {
    return <Alert type="info" showIcon message={t('ozDiagSelectCompany')} />;
  }

  return (
    <div>
      <Alert
        type="info"
        showIcon
        className="mb-3"
        message={t('ozDiagReadOnly')}
        description={t('ozDiagInfoDesc')}
      />
      <Segmented
        className="mb-3"
        value={mode}
        onChange={setMode}
        options={[
          { label: t('ozDiagHistory'), value: 'history' },
          { label: t('ozDiagTargets'), value: 'targets' },
        ]}
      />
      {mode === 'history' ? (
        <HistoricalEarningsView companyId={companyId} />
      ) : (
        <AbsoftTargetsView companyId={companyId} />
      )}
    </div>
  );
}
